import { useContext } from 'react';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from '../globalization/useTranslation';
import { LockUnlockResponse, LockUnlockDocumentsInfoRequest } from '@cs-cms/types';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import { useSearchContext } from '../../contexts/SearchContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { searchKeys } from '../../queryKeys/search';
import { useNotificationContext } from '../useNotificationContext';

export function useUnlockDocuments(): UseMutationResult<
  LockUnlockResponse,
  unknown,
  { dto: LockUnlockDocumentsInfoRequest }
> {
  const servicesCtx = useContext(ServicesContext);
  const { addCenterNotification } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();

  const unlockDocuments = (data: { dto: LockUnlockDocumentsInfoRequest }): Promise<LockUnlockResponse> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.unlockDocuments(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.unlockDocuments,
    mutationFn: (data: { dto: LockUnlockDocumentsInfoRequest }) => unlockDocuments(data),
    onSuccess: async data => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentSuccessMessage', {
          changed: data.changed.length,
          total: data.changed.length + data.skipped.length
        }),
        timestamp: new Date(),
        type: 'success'
      });
      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }
    },
    onError: () => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
